import React, { useEffect, useState } from 'react';
import type { PlayerContactProps, PlayerFollowerProps, PublicPlayerProps } from '../../types';
import { useContacts } from '../../SocialComponents';
import { useColors } from '../../constants/useColors';
import { SocialComponentApi } from '../../SocialComponents/api';
import { PlayerRecommenderApi } from '../api';
import { ActivityIndicator, FlatList, Image, Linking, Platform } from 'react-native';
import { Button, Text, View } from '../../Components/Themed';
import PlayerFollowButton from '../../SocialComponents/PlayerFollowButton';
import { Icons } from '../../Components';


type ContactsCardProps = {
    player_id?:string,
    referral_code?:string,
    onSelectContact:(pc:PlayerContactProps, p?:PublicPlayerProps) => void,
    onInviteContact:(pc:PlayerContactProps, p?:PublicPlayerProps) => void
    onFullSearch:() => void
}

const ContactsCard = ({ player_id, referral_code, onSelectContact, onFullSearch, onInviteContact }:ContactsCardProps) => {
    const Colors = useColors();
    const { is_available, permission, checkPermissions, compareContacts, requestPermissions } = useContacts({ referral_code });
    const [ contacts, setContacts ] = useState<{
        loading:boolean,
        checked:boolean
    }>({
        loading:false,
        checked: false
    });
    const [ contact_data, setContactData ] = useState<{
        contacts_loading:boolean,
        player_contacts:PlayerContactProps[],
        players:PublicPlayerProps[],
        player_followers:PlayerFollowerProps[]
    }>({
        contacts_loading: false,
        player_contacts: [],
        players: [],
        player_followers:[]
    });
    const { contacts_loading, players, player_followers, player_contacts } = contact_data
    let visible_contacts = player_contacts.filter(pc => pc.connected_player_id);
    if(visible_contacts.length == 0){
        visible_contacts = player_contacts.filter(pc => pc.sub_type == 'phone').slice(0, 10)
    }

    useEffect(() => {
        PlayerRecommenderApi.setEnvironment();
        if(!player_id){ return }
        if(!is_available){ return }
        if(!permission?.granted){ return }
        if(contacts.checked){ return }
        checkContacts();
    },[player_id, permission?.granted, is_available])

    const checkContacts = async() => {
        if(contacts.checked){ return }
        if(!player_id){ return }
        setContactData({ ...contact_data, contacts_loading:true });
        const pcs = await compareContacts(player_id);
        const player_ids = [ ...new Set(pcs.map(c => c.connected_player_id ?? '0')) ]
        const ps = await SocialComponentApi.getPlayersByIds(player_ids);
        let pfs = await PlayerRecommenderApi.getBulkPlayerfollowers(ps.map(p => p.player_id));
        setContacts({ loading:false, checked:true })
        setContactData({
            ...contact_data,
            contacts_loading: false,
            players: ps,
            player_followers: pfs,
            player_contacts: pcs
        });
    }

    const handleSelectContacts = async() => {
        if(!is_available){ return alert('Unable to process') }
        let checked_perm = await checkPermissions();
        if(!checked_perm?.granted){
            if(!checked_perm?.canAskAgain){
                if (Platform.OS === 'ios') {
                    Linking.openURL('app-settings:');
                  } else {
                    Linking.openSettings();
                  }
                return
            }
            checked_perm = await requestPermissions();
            if(!checked_perm?.granted){ return alert('Sorry! We are not able to check your contacts.  Please enable them in your settings and try again') }
        }
        return
    }

    const renderContacts = (data:{ item:PlayerContactProps, index:number }) => {
        const pf = player_followers.find(pf => pf.following_id == data.item.connected_player_id);
        const player = players.find(p => p.player_id == data.item.connected_player_id);
        
        return (
            <Button float style={{ padding:0, margin:4 }} onPress={() => onSelectContact(data.item, player)}>
                <Image
                    source={{ uri: player?.profile_pic &&  player?.profile_pic != '' ?  player?.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                    style={{ height:75, width:115, borderTopRightRadius:8, borderTopLeftRadius:8 }}
                    resizeMode='cover'
                />
                <View style={{ flexGrow:1, padding:5}}>
                    <Text theme='h2' textAlign='center' style={{ maxWidth:100 }}>{data.item.first_name} {data.item.last_name}</Text>
                    {player?
                    <Text theme='description' textAlign='center' style={{ maxWidth:100 }}>@{player.username}</Text>
                    :<></>}
                </View>
                <View transparent style={{ padding:5, borderTopWidth:1, borderColor:Colors.borders.light }}>
                    {player ?
                    <PlayerFollowButton
                        player_id={player.player_id}
                        following={pf}
                        //follower={pf}
                        onFollowingUpdate={(new_pf) => {
                            setContactData({ 
                                ...contact_data, 
                                player_followers: player_followers.filter(pf => pf.player_follower_id != new_pf.player_follower_id).concat(new_pf)
                            })
                        }}
                    />
                    :
                    <Button
                        title='Invite!'
                        type='success'
                        style={{ padding:10 }}
                        onPress={() => onInviteContact(data.item)}
                    />
                    }
                </View>
            </Button>  
        )
    }

    return (
        <View>
            {permission?.granted ?
            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:15 }}>
                <Icons.ContactsIcon color={Colors.text.h1} size={26} />
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>Your Contacts</Text>
                    <Text theme='description' style={{ marginTop:3 }}>Find your contacts that are currently using BettorEdge!</Text>
                </View>
                <Button 
                    title='See All'
                    type='text'
                    transparent
                    style={{ padding:10 }}
                    onPress={() => onFullSearch()}
                />
            </View>
            :
            <Button float style={{ flexDirection:'row', alignItems:'center', padding:15, margin:5 }} onPress={() => handleSelectContacts()}>
                <Icons.ContactsIcon size={28} color={Colors.text.h1} /> 
                <View transparent style={{ flex:1, marginLeft:5, paddingLeft:5, marginRight:10, borderLeftWidth:1, borderColor:Colors.borders.light }}>
                    <Text theme='h1'>Need Help Finding People?</Text>
                    {!permission?.canAskAgain ?
                    <Text theme='warning'>Enable access to contacts in settings.  We will never contact them.</Text>
                    :
                    <Text theme='description'>Press here and we will find existing players in your contacts.  We will never contact them</Text>
                    }
                </View>
                <Button
                    type='success'
                    title='Start'
                    disabled
                    onPress={() => console.log('')}
                />
            </Button>
            }
            <View style={{ padding:10, flexDirection:'row' }}>
                {contacts_loading ?
                <ActivityIndicator
                    style={{ padding:10, alignSelf:'center' }}
                    color={Colors.text.h1}
                    size={'large'}
                />
                :<></>}
                <FlatList
                    key='recommended_players'
                    keyExtractor={(item) => item.player_contact_id.toString()}
                    data={visible_contacts}
                    horizontal
                    renderItem={renderContacts}
                />
            </View>
        </View>
    )
}

export default ContactsCard